import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import type { ConsentStatementRef } from "@/lib/interviewTypes";

// PUBLICITY RELEASE step (E-SIGNATURE) — the SECOND signed document, distinct from
// the written biometric/privacy consent (WrittenConsent.tsx). Per counsel (Gene),
// the subject signs a separate publicity / right-of-publicity release (names
// "digital replicas", grounded in distinct right-of-publicity law, survives death,
// must come from the SUBJECT not the buyer). Like written consent, it completes
// BEFORE the spoken ConsentGate trio (consent/state/dob) and therefore before any
// recording of any kind — it sits right after the written consent, before the
// geography gate.
//
// This is a WRITTEN e-signature: a typed full legal name + an attestation checkbox.
// NOT spoken/recorded — no microphone, no MediaRecorder, no STT. It reuses
// ConsentGate's visual scaffolding (text-center container, uppercase label,
// bordered box, Button, error card) but none of its recording logic.
//
// The disclosure TEXT and its VERSION are DB-BACKED: they arrive as the optional
// `publicityStatement` ({ id, text }) surfaced from the active 'publicity_release'
// row in interview_consent_statements (rides the same consent_statements payload
// the final-consent step uses via .legacylens). Counsel owns the wording; it lives
// in the DB row (swapped via the versioning path), NOT in this file.
//
// There is intentionally NO signable hardcoded fallback: if no active publicity
// statement is present, the component BLOCKS signing with an explicit message
// rather than letting the subject sign against unknown text. On submit the server
// re-validates the version against the active row (mismatch -> 409), so the client
// can never record a signature against text we can't prove was shown.

// UI chrome only (NOT release language — that is the DB-backed statement text).
const PUBLICITY_RELEASE_HEADING = "Publicity release";
const PUBLICITY_RELEASE_NAME_LABEL = "Type your full legal name to sign";
const PUBLICITY_RELEASE_ATTEST_LABEL =
  "I have read and agree to the publicity release above";
const PUBLICITY_RELEASE_SIGN_LABEL = "Sign publicity release";
const PUBLICITY_RELEASE_UNAVAILABLE =
  "The publicity release isn't available right now. Please contact LegacyLens.";

type Phase = "checking" | "ready" | "submitting" | "error";

// Fields this component reads off interview-session-status. The endpoint returns
// more (status, capture_medium, progress); we read publicity_release_accepted_at
// (the "already signed" signal) and — for the 409 refetch — consent_statements.publicityRelease.
type SessionStatusResponse = {
  ok: boolean;
  publicity_release_accepted_at: string | null;
  consent_statements?: { publicityRelease?: ConsentStatementRef | null } | null;
};

type AcceptResponse = {
  ok: boolean;
  publicity_release_accepted_at?: string;
  already_set?: boolean;
};

export type PublicityReleaseProps = {
  resumeToken: string;
  // The active publicity-release disclosure ({ id, text }) from the consent_statements
  // payload. null/absent => no active statement => signing is blocked (see render).
  publicityStatement?: ConsentStatementRef | null;
  onAccepted: () => void;
  onError: (message: string) => void;
  onSessionExpired: () => void;
};

// Pull the HTTP status off a supabase-js FunctionsHttpError (wrapped Response on
// error.context), matching the pattern used across the interview components.
const statusOf = (error: unknown): number | undefined => {
  const ctx = (error as { context?: Response }).context;
  return ctx && typeof ctx.status === "number" ? ctx.status : undefined;
};

const PublicityRelease = ({
  resumeToken,
  publicityStatement = null,
  onAccepted,
  onError,
  onSessionExpired,
}: PublicityReleaseProps) => {
  const [phase, setPhase] = useState<Phase>("checking");
  // The DB-backed disclosure under review. Initialized from the prop; refreshed on
  // a 409 version-mismatch so the subject re-reads + re-signs the CURRENT text.
  const [statement, setStatement] = useState<ConsentStatementRef | null>(publicityStatement);
  // E-signature affordance: typed full legal name + a separate attestation check.
  const [name, setName] = useState("");
  const [attested, setAttested] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Which action the error card's "Try again" should re-fire.
  const [lastAction, setLastAction] = useState<"check" | "submit">("check");

  // Insulate the callbacks from parent re-renders (identities change every render
  // but the async work must not restart) — same ref pattern as ConsentGate.
  const cbRef = useRef({ onAccepted, onError, onSessionExpired });
  cbRef.current = { onAccepted, onError, onSessionExpired };

  // Guards against setState/callbacks after unmount.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Entry check: has the publicity release already been recorded (subject resuming
  // mid-flow, before the spoken gates)? If so, skip straight ahead. Also the retry
  // target when the check itself fails. The publicity_release_accepted_at timestamp
  // is the "already signed" signal.
  const checkEntry = async () => {
    setPhase("checking");
    setErrorMsg(null);
    const { data, error } = await supabase.functions.invoke<SessionStatusResponse>(
      "interview-session-status",
      { headers: { Authorization: `Bearer ${resumeToken}` } },
    );
    if (!mountedRef.current) return;
    if (error) {
      const status = statusOf(error);
      if (status === 401 || status === 410) {
        cbRef.current.onSessionExpired();
        return;
      }
      setLastAction("check");
      setErrorMsg("We couldn't load this step. Please try again.");
      setPhase("error");
      return;
    }
    if (data?.publicity_release_accepted_at) {
      cbRef.current.onAccepted();
      return;
    }
    setPhase("ready");
  };

  // Refetch the CURRENT active publicity statement (after a 409 version mismatch) so
  // the subject sees + signs the up-to-date text/version. If the row is gone,
  // statement becomes null and the render blocks signing.
  const refetchStatement = async () => {
    const { data, error } = await supabase.functions.invoke<SessionStatusResponse>(
      "interview-session-status",
      { headers: { Authorization: `Bearer ${resumeToken}` } },
    );
    if (!mountedRef.current) return;
    if (error) {
      const status = statusOf(error);
      if (status === 401 || status === 410) cbRef.current.onSessionExpired();
      // Non-fatal otherwise: keep the (stale) statement; the message still tells
      // them to review, and a re-submit will 409 again rather than mis-record.
      return;
    }
    setStatement(data?.consent_statements?.publicityRelease ?? null);
  };

  const submit = async () => {
    // Never sign without a resolved statement (the button is hidden without one,
    // but be defensive).
    if (!statement) return;
    setPhase("submitting");
    setErrorMsg(null);
    const { data, error } = await supabase.functions.invoke<AcceptResponse>(
      "interview-accept-publicity-release",
      {
        headers: { Authorization: `Bearer ${resumeToken}` },
        body: { signature_name: name.trim(), version: statement.id },
      },
    );
    if (!mountedRef.current) return;
    if (error) {
      const status = statusOf(error);
      if (status === 401 || status === 410) {
        cbRef.current.onSessionExpired();
        return;
      }
      if (status === 409) {
        // The active version changed while they were on the page. Pull the current
        // text/version, clear the attestation, and return them to signing so they
        // re-read the up-to-date form (prevents a stale re-sign / 409 loop).
        await refetchStatement();
        if (!mountedRef.current) return;
        setAttested(false);
        setErrorMsg("The publicity release was updated. Please review the current version and sign again.");
        setPhase("ready");
        return;
      }
      if (status === 400) {
        // Shouldn't happen given the client-side gating; handle defensively.
        setLastAction("submit");
        setErrorMsg("Please check your entry and try again.");
        setPhase("error");
        return;
      }
      // 500 / other non-ok.
      setLastAction("submit");
      setErrorMsg("We couldn't save your publicity release. Please try again.");
      setPhase("error");
      return;
    }
    if (data?.ok) {
      cbRef.current.onAccepted();
      return;
    }
    setLastAction("submit");
    setErrorMsg("We couldn't save your publicity release. Please try again.");
    setPhase("error");
  };

  useEffect(() => {
    void checkEntry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeToken]);

  if (phase === "checking" || phase === "submitting") {
    return (
      <div className="text-center" aria-live="polite">
        <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-muted">
          <Loader2 className="h-10 w-10 text-primary animate-spin" />
        </div>
        <p className="mt-6 text-2xl font-display text-foreground">
          {phase === "submitting" ? "Saving your publicity release…" : "One moment…"}
        </p>
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="text-center">
        <p className="text-base text-destructive">
          {errorMsg ?? "Something went wrong. Please try again."}
        </p>
        <Button
          size="lg"
          variant="outline"
          className="mt-4"
          onClick={() => (lastAction === "submit" ? void submit() : void checkEntry())}
        >
          Try again
        </Button>
      </div>
    );
  }

  // phase === "ready". DB-backed text is required: if no active publicity statement
  // surfaced, BLOCK signing with an explicit message — never sign against unknown
  // text, and never fall back to a hardcoded statement.
  if (!statement) {
    return (
      <div className="text-center">
        <p className="text-base text-destructive max-w-md mx-auto">
          {PUBLICITY_RELEASE_UNAVAILABLE}
        </p>
      </div>
    );
  }

  const canSign = name.trim().length > 0 && attested;

  return (
    <div className="text-center" aria-live="polite">
      <div className="mb-6 text-sm uppercase tracking-wide text-muted-foreground">
        Publicity release
      </div>
      <h1 className="text-2xl font-display text-foreground">
        {PUBLICITY_RELEASE_HEADING}
      </h1>

      {errorMsg ? (
        <p className="mt-4 text-base text-destructive max-w-md mx-auto">{errorMsg}</p>
      ) : null}

      {/* Full disclosure text renders here, above the signature controls. */}
      <div className="mt-6 rounded-2xl border border-border px-6 py-5 text-left max-w-md mx-auto">
        <p className="text-base text-foreground whitespace-pre-line">
          {statement.text}
        </p>
      </div>

      <div className="mt-6 max-w-md mx-auto text-left">
        <label
          className="block text-base text-foreground"
          htmlFor="pr-signature-name"
        >
          {PUBLICITY_RELEASE_NAME_LABEL}
        </label>
        <input
          id="pr-signature-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="name"
          className="mt-2 w-full rounded-lg border border-border bg-background px-4 py-3 text-base text-foreground"
        />
      </div>

      <label className="mt-6 flex items-start gap-3 text-left max-w-md mx-auto cursor-pointer">
        <input
          type="checkbox"
          checked={attested}
          onChange={(e) => setAttested(e.target.checked)}
          className="mt-1 h-5 w-5 shrink-0 rounded border-border"
        />
        <span className="text-base text-foreground">
          {PUBLICITY_RELEASE_ATTEST_LABEL}
        </span>
      </label>

      <div className="mt-8">
        <Button
          size="lg"
          onClick={() => void submit()}
          disabled={!canSign}
          className="text-xl px-10 py-6 h-auto rounded-2xl"
        >
          {PUBLICITY_RELEASE_SIGN_LABEL}
        </Button>
      </div>
    </div>
  );
};

export default PublicityRelease;
